import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { AlertCircle, ShieldCheck } from 'lucide-react';
import { authService } from '@/services/auth';
import { getApiError } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { LoadingState } from '@/components/ui';

interface MfaVerifyForm {
  code: string;
}

export function MfaSetupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const updateUser = useAuthStore((s) => s.updateUser);
  const [error, setError] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  // Each call to /mfa/setup issues a new secret, so never refetch or retry it automatically.
  const { data: mfaSetup, isLoading, error: setupError } = useQuery({
    queryKey: ['mfa-setup'],
    queryFn: authService.setupMfa,
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });

  // Render the otpauth:// URI locally: it contains the TOTP secret and must not be sent to a third party.
  useEffect(() => {
    if (!mfaSetup?.qrCode) return;
    let cancelled = false;
    QRCode.toDataURL(mfaSetup.qrCode, { width: 200, margin: 1 })
      .then((url) => !cancelled && setQrDataUrl(url))
      .catch(() => !cancelled && setQrDataUrl(null));
    return () => {
      cancelled = true;
    };
  }, [mfaSetup?.qrCode]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<MfaVerifyForm>();

  const verifyMutation = useMutation({
    mutationFn: authService.verifyMfa,
    onSuccess: () => {
      updateUser({ mfaEnabled: true });
      navigate('/');
    },
    onError: (err) => {
      setError(getApiError(err).message);
    },
  });

  const onSubmit = (data: MfaVerifyForm) => {
    setError(null);
    verifyMutation.mutate(data.code);
  };

  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <div>
      <div className="mb-8">
        <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-primary-50">
          <ShieldCheck className="h-6 w-6 text-primary-600" />
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900">{t('auth.mfa.setup')}</h1>
        <p className="mt-2 text-sm text-gray-500">{t('auth.mfa.setupDescription')}</p>
      </div>

      {(error || setupError) && (
        <div className="alert-danger mb-6" role="alert">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <span>{error ?? getApiError(setupError).message}</span>
        </div>
      )}

      {mfaSetup && (
        <div className="mb-6 flex flex-col items-center gap-4 rounded-xl border border-gray-200 bg-gray-50 p-5 sm:flex-row sm:items-start">
          <div className="flex h-44 w-44 shrink-0 items-center justify-center rounded-lg bg-white p-2 ring-1 ring-gray-200">
            {qrDataUrl && <img src={qrDataUrl} alt="MFA QR Code" className="h-full w-full" />}
          </div>
          {mfaSetup.secret && (
            <div className="min-w-0 text-center sm:text-start">
              <p className="text-xs text-gray-500">{t('auth.mfaManualEntry')}</p>
              <code
                dir="ltr"
                className="mt-2 block break-all rounded-md bg-white px-3 py-2 font-mono text-sm text-gray-900 ring-1 ring-gray-200"
              >
                {mfaSetup.secret}
              </code>
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        <div>
          <label htmlFor="code" className="label">
            {t('auth.mfa.enterCode')}
          </label>
          <input
            type="text"
            id="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            dir="ltr"
            placeholder="000000"
            className={`${errors.code ? 'input-error' : 'input'} py-3 text-center text-2xl font-semibold tracking-[0.5em]`}
            {...register('code', {
              required: t('auth.fieldRequired'),
              pattern: { value: /^\d{6}$/, message: t('auth.mfaSubtitle') },
            })}
          />
          {errors.code && <p className="field-error">{errors.code.message}</p>}
        </div>

        <button type="submit" disabled={verifyMutation.isPending || !mfaSetup} className="btn-primary btn-lg w-full">
          {verifyMutation.isPending ? t('common.loading') : t('auth.mfa.verify')}
        </button>
        <button type="button" onClick={() => navigate(-1)} className="btn-ghost w-full">
          {t('common.cancel')}
        </button>
      </form>
    </div>
  );
}
